import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY, IMAGES } from "../content";
import { EASE } from "../motion";

const LightboxCtx = createContext(() => {});
export const useLightbox = () => useContext(LightboxCtx);

export function LightboxProvider({ children }) {
  const [index, setIndex] = useState(-1);
  const open = useCallback((key) => {
    const i = GALLERY.findIndex((g) => g.key === key);
    setIndex(i >= 0 ? i : 0);
  }, []);
  const close = useCallback(() => setIndex(-1), []);
  const step = useCallback((d) => setIndex((i) => (i + d + GALLERY.length) % GALLERY.length), []);

  useEffect(() => {
    if (index < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, close, step]);

  useEffect(() => {
    const active = index >= 0;
    if (active) {
      document.body.style.overflow = "hidden";
      if (window.__lenis) window.__lenis.stop();
    } else {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    }
  }, [index]);

  const item = index >= 0 ? GALLERY[index] : null;

  return (
    <LightboxCtx.Provider value={open}>
      {children}
      <AnimatePresence>
        {item && (
          <motion.div
            data-testid="lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={item.caption}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="fixed inset-0 z-[110] bg-[#0F0F0F]/90 backdrop-blur-md flex items-center justify-center p-5 sm:p-12"
            onClick={close}
          >
            <button
              data-testid="lightbox-close"
              onClick={close}
              aria-label="Close gallery"
              className="absolute top-5 right-5 sm:top-8 sm:right-8 w-11 h-11 rounded-full border border-[#F5F3EE]/25 text-[#F5F3EE]/80 flex items-center justify-center transition-[background-color,color] duration-300 hover:bg-[#F5F3EE] hover:text-[#0F0F0F] cursor-pointer z-10"
            >
              <X size={18} />
            </button>

            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photograph"
              className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-[#F5F3EE]/25 text-[#F5F3EE]/80 flex items-center justify-center transition-[background-color,color] duration-300 hover:bg-[#F5F3EE] hover:text-[#0F0F0F] cursor-pointer z-10"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photograph"
              className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full border border-[#F5F3EE]/25 text-[#F5F3EE]/80 flex items-center justify-center transition-[background-color,color] duration-300 hover:bg-[#F5F3EE] hover:text-[#0F0F0F] cursor-pointer z-10"
            >
              <ChevronRight size={18} />
            </button>

            <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <AnimatePresence mode="sync">
                  <motion.img
                    key={item.key}
                    src={IMAGES[item.key]}
                    alt={item.caption}
                    initial={{ opacity: 0, scale: 0.985 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: EASE }}
                    className="w-full max-h-[76vh] object-cover"
                  />
                </AnimatePresence>
              </div>
              <motion.div
                key={`${item.key}-caption`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: EASE, delay: 0.15 }}
                className="flex items-baseline justify-between gap-6 mt-5 px-1"
              >
                <p data-testid="lightbox-caption" className="font-serif italic text-lg sm:text-xl text-[#F5F3EE]/90">{item.caption}</p>
                <p className="text-[10px] font-semibold tracking-[0.28em] text-[#F5F3EE]/40 shrink-0">
                  {String(index + 1).padStart(2, "0")} / {String(GALLERY.length).padStart(2, "0")}
                </p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </LightboxCtx.Provider>
  );
}
